import React, { useState, useEffect, useCallback } from 'react';
import { PlusCircle, Edit3, Trash2 } from 'lucide-react';
import '../styles/CommonStyles.css';
import { Modal, ConfirmationModal } from './Helpers';
import { addMeterReading, fetchMeterReadings, updateMeterReading, deleteMeterReading } from '../api/meterReadingApi';
import { fetchApartments } from '../api/apartmentApi';
import { fetchBuildings } from '../api/buildingApi';
import { fetchBlocks } from '../api/blockApi';

const MeterReadingsView = ({ showToast, currentUser }) => {
  const [meterReadings, setMeterReadings] = useState([]);
  const [meterReadingForm, setMeterReadingForm] = useState({
    apartmentId: '',
    meterType: 'su',
    consumption: '',
    period: new Date().toISOString().slice(0, 7),
  });
  const [apartments, setApartments] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingReading, setEditingReading] = useState(null);
  const [confirmDeleteModal, setConfirmDeleteModal] = useState({ isOpen: false, readingId: null });

  const loadInitialData = useCallback(async () => {
    if (!currentUser?.buildingId) {
      showToast('Kullanıcıya atanmış bina bulunamadı.', 'error');
      setIsDataLoading(false);
      return;
    }
    setIsDataLoading(true);
    try {
      console.log('Fetching initial data for buildingId:', currentUser.buildingId);
      const buildingId = currentUser.buildingId;
      
      // URL parametresi olarak buildingId'yi geçiyoruz
      const [apts, blds, blks] = await Promise.all([
        fetchApartments({ buildingId }),
        fetchBuildings(),
        fetchBlocks(buildingId), // Doğrudan buildingId'yi geçiyoruz
      ]);
      
      console.log('Fetched apartments:', apts);
      console.log('Fetched buildings:', blds);
      console.log('Fetched blocks:', blks);
      
      // Veri formatını normalize et
      const normalizedApts = Array.isArray(apts) ? apts : apts.recordset ?? apts.data ?? apts.apartments ?? [];
      const normalizedBlds = Array.isArray(blds) ? blds : blds.recordset ?? blds.data ?? [];
      const normalizedBlks = Array.isArray(blks) ? blks : blks.recordset ?? blks.data ?? [];
      
      setApartments(normalizedApts);
      setBuildings(normalizedBlds);
      setBlocks(normalizedBlks);
    } catch (error) {
      console.error('Error loading initial data:', error);
      showToast("Gerekli veriler yüklenirken hata: " + error.message, "error");
    } finally {
      setIsDataLoading(false);
    }
  }, [showToast, currentUser]);

  const loadMeterReadings = useCallback(async () => {
    if (!currentUser?.buildingId) {
      showToast('Kullanıcıya atanmış bina bulunamadı.', 'error');
      setMeterReadings([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      console.log('Sayaç okumaları yükleniyor, buildingId:', currentUser.buildingId);
      const data = await fetchMeterReadings(currentUser.buildingId);
      
      // Veri formatını kontrol et ve normalize et
      const normalizedData = Array.isArray(data) ? data : data?.recordset ?? data?.data ?? [];
      console.log('Alınan sayaç okumaları:', normalizedData);
      
      // Veri içeriğini kontrol et
      if (normalizedData.length > 0) {
        console.log('Örnek sayaç okuması:', normalizedData[0]);
      }
      
      setMeterReadings(normalizedData);
    } catch (error) {
      console.error('Sayaç okumaları yüklenirken hata:', error);
      showToast("Sayaç okumaları yüklenirken hata: " + error.message, "error");
    } finally {
      setIsLoading(false);
    }
  }, [showToast, currentUser]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (!isDataLoading) {
      loadMeterReadings();
    }
  }, [isDataLoading, loadMeterReadings]);

  // Daire numarasını bul (ApartmentId veya apartmentId kullanarak)
  const getApartmentNumber = (apartmentId) => {
    if (!apartmentId || !apartments.length) return 'Bilinmiyor';
    
    // Farklı ID alanlarını kontrol et
    const apt = apartments.find(apt => {
      const aptId = apt.id || apt.apartmentId || apt.ApartmentId;
      const compareId = parseInt(apartmentId);
      return aptId === compareId;
    });
    
    if (apt) {
      const aptNumber = apt.apartmentNumber || apt.ApartmentNumber;
      console.log(`Daire bulundu: ID=${apartmentId}, Numara=${aptNumber}`);
      return aptNumber || 'Bilinmiyor';
    } else {
      console.log(`Daire bulunamadı: ID=${apartmentId}`);
      return 'Bilinmiyor';
    }
  };

  // Blok adını bul (BlockId veya blockId kullanarak)
  const getBlockName = (blockId) => {
    if (!blockId || !blocks.length) return 'Bilinmiyor';
    
    // Farklı ID alanlarını kontrol et
    const block = blocks.find(blk => {
      const blkId = blk.id || blk.blockId || blk.BlockId;
      const compareId = parseInt(blockId);
      return blkId === compareId;
    });
    
    if (block) {
      const blkName = block.name || block.Name;
      console.log(`Blok bulundu: ID=${blockId}, Ad=${blkName}`);
      return blkName || 'Bilinmiyor';
    } else {
      console.log(`Blok bulunamadı: ID=${blockId}`);
      return 'Bilinmiyor';
    }
  };
  
  // Dairenin blok ID'sini bul (ApartmentId veya apartmentId kullanarak)
  const getBlockIdForApartment = (apartmentId) => {
    if (!apartmentId || !apartments.length) return null;
    
    // Farklı ID alanlarını kontrol et
    const apt = apartments.find(apt => {
      const aptId = apt.id || apt.apartmentId || apt.ApartmentId;
      const compareId = parseInt(apartmentId);
      return aptId === compareId;
    });
    
    if (apt) {
      const blkId = apt.blockId || apt.BlockId;
      console.log(`Daire için blok bulundu: DaireID=${apartmentId}, BlokID=${blkId}`);
      return blkId;
    } else {
      console.log(`Daire için blok bulunamadı: DaireID=${apartmentId}`);
      return null;
    }
  };

  const getBuildingName = (id) => buildings.find(bld => bld.id === id)?.name || 'Bilinmiyor';

  const handleAddMeterReading = async (e) => {
    e.preventDefault();
    const { apartmentId, meterType, consumption, period } = meterReadingForm;
    if (!apartmentId || !meterType || !consumption || !period) {
      showToast("Tüm alanları doldurun.", "error");
      return;
    }
    setIsLoading(true);
    try {
      const readingData = {
        apartmentId: parseInt(apartmentId),
        buildingId: currentUser.buildingId,
        meterType,
        period,
        consumption: parseFloat(consumption),
      };
      await addMeterReading(readingData);
      showToast('Sayaç okuması başarıyla eklendi.', 'success');
      setMeterReadingForm({ apartmentId: '', meterType: 'su', consumption: '', period: new Date().toISOString().slice(0, 7) });
      loadMeterReadings();
    } catch (error) {
      showToast('Sayaç okuması eklenirken hata: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (reading) => {
    console.log('Düzenleme için okuma verisi:', reading);
    
    // ApartmentId veya apartmentId değerini kontrol et
    const apartmentId = reading.ApartmentId !== undefined ? reading.ApartmentId : reading.apartmentId;
    
    if (apartmentId === undefined || apartmentId === null) {
      showToast('Daire ID bilgisi eksik', 'error');
      console.error('Daire ID bulunamadı:', reading);
      return;
    }
    
    // Consumption veya consumption değerini kontrol et
    const consumption = reading.Consumption !== undefined ? reading.Consumption : reading.consumption;
    
    if (consumption === undefined || consumption === null) {
      showToast('Tüketim bilgisi eksik', 'error');
      console.error('Tüketim bilgisi bulunamadı:', reading);
      return;
    }
    
    // ReadingId veya readingId değerini kontrol et
    const readingId = reading.ReadingId !== undefined ? reading.ReadingId : reading.readingId;
    
    // MeterType veya meterType değerini kontrol et
    const meterType = reading.MeterType !== undefined ? reading.MeterType : reading.meterType;
    
    // Period veya period değerini kontrol et
    const period = reading.Period !== undefined ? reading.Period : reading.period;
    
    // Değerleri string'e dönüştür
    const apartmentIdStr = String(apartmentId);
    const consumptionStr = String(consumption);
    
    setEditingReading({
      ...reading,
      readingId: readingId,
      apartmentId: apartmentIdStr,
      consumption: consumptionStr,
      period: period || new Date().toISOString().slice(0, 7),
      meterType: meterType
    });
    
    setEditModalOpen(true);
  };

  const handleUpdateMeterReading = async (e) => {
    e.preventDefault();
    if (!editingReading) return;
    
    // ReadingId veya readingId değerini kontrol et
    const readingId = editingReading.ReadingId !== undefined ? editingReading.ReadingId : editingReading.readingId;
    
    const { apartmentId, meterType, consumption, period } = editingReading;
    if (!apartmentId || !meterType || !consumption || !period) {
      showToast("Tüm alanları doldurun.", "error");
      return;
    }
    setIsLoading(true);
    try {
      const updatedData = {
        apartmentId: parseInt(apartmentId),
        meterType,
        period,
        consumption: parseFloat(consumption),
        buildingId: currentUser.buildingId,
      };
      await updateMeterReading(readingId, updatedData);
      showToast('Sayaç okuması başarıyla güncellendi.', 'success');
      setEditModalOpen(false);
      setEditingReading(null);
      loadMeterReadings();
    } catch (error) {
      showToast('Sayaç okuması güncellenirken hata: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteMeterReading = async () => {
    // readingId'nin geçerli olup olmadığını kontrol et
    if (!confirmDeleteModal.readingId) {
      showToast('Geçerli bir sayaç okuma ID\'si bulunamadı', 'error');
      setConfirmDeleteModal({ isOpen: false, readingId: null });
      return;
    }
    
    // buildingId'nin geçerli olup olmadığını kontrol et
    if (!currentUser?.buildingId) {
      showToast('Kullanıcıya atanmış bina bulunamadı', 'error');
      setConfirmDeleteModal({ isOpen: false, readingId: null });
      return;
    }
    
    setIsLoading(true);
    try {
      console.log('Silme işlemi başlatılıyor, readingId:', confirmDeleteModal.readingId, 'buildingId:', currentUser.buildingId);
      
      // buildingId parametresini de geçiyoruz
      await deleteMeterReading(confirmDeleteModal.readingId, currentUser.buildingId);
      
      showToast('Sayaç okuması başarıyla silindi.', 'success');
      setConfirmDeleteModal({ isOpen: false, readingId: null });
      loadMeterReadings();
    } catch (error) {
      console.error('Silme hatası:', error);
      showToast('Sayaç okuması silinirken hata: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (isDataLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        <p className="ml-3 text-gray-600">Veriler yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow">
      {isLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-30">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <p className="ml-3 text-gray-700">İşlem yapılıyor...</p>
        </div>
      )}
      <h1 className="text-2xl font-semibold mb-6">Sayaç Okumaları Yönetimi</h1>

      {/* Sayaç Okuması Ekleme Formu */}
      <div className="mb-6 p-4 border rounded-lg bg-gray-50">
        <h2 className="text-xl font-semibold mb-3">Yeni Sayaç Okuması Ekle</h2>
        <form onSubmit={handleAddMeterReading}>
          <div className="grid md:grid-cols-4 gap-4">
            <div>
              <label htmlFor="apartmentId" className="label-form-style">Daire</label>
              <select
                id="apartmentId"
                name="apartmentId"
                value={meterReadingForm.apartmentId}
                onChange={(e) => setMeterReadingForm({ ...meterReadingForm, apartmentId: e.target.value })}
                className="input-form-style"
                required
              >
                <option value="">Daire Seçin</option>
                {apartments
                  .filter(apt => {
                    const aptBuildingId = apt.buildingId || apt.BuildingId;
                    return aptBuildingId === currentUser.buildingId;
                  })
                  .map(apt => {
                    const aptId = apt.id || apt.apartmentId || apt.ApartmentId;
                    const aptNumber = apt.apartmentNumber || apt.ApartmentNumber;
                    const blkId = apt.blockId || apt.BlockId;
                    return (
                      <option key={aptId} value={aptId}>
                        {aptNumber} ({getBlockName(blkId)})
                      </option>
                    );
                  })}
              </select>
            </div>
            <div>
              <label htmlFor="meterType" className="label-form-style">Sayaç Türü</label>
              <select
                id="meterType"
                name="meterType"
                value={meterReadingForm.meterType}
                onChange={(e) => setMeterReadingForm({ ...meterReadingForm, meterType: e.target.value })}
                className="input-form-style"
                required
              >
                <option value="su">Su</option>
                <option value="yakit">Yakıt</option>
              </select>
            </div>
            <div>
              <label htmlFor="consumption" className="label-form-style">Tüketim (m³/kWh)</label>
              <input
                type="number"
                id="consumption"
                name="consumption"
                step="0.01"
                value={meterReadingForm.consumption}
                onChange={(e) => setMeterReadingForm({ ...meterReadingForm, consumption: e.target.value })}
                className="input-form-style"
                required
              />
            </div>
            <div>
              <label htmlFor="period" className="label-form-style">Dönem (YYYY-MM)</label>
              <input
                type="month"
                id="period"
                name="period"
                value={meterReadingForm.period}
                onChange={(e) => setMeterReadingForm({ ...meterReadingForm, period: e.target.value })}
                className="input-form-style"
                required
              />
            </div>
          </div>
          <button type="submit" className="btn-primary-green-style mt-4" disabled={isLoading}>
            <PlusCircle size={18} className="mr-2" /> Sayaç Okumasını Kaydet
          </button>
        </form>
      </div>

      {/* Sayaç Okumaları Listesi */}
      <h2 className="text-xl font-semibold mb-4">Mevcut Sayaç Okumaları</h2>
      {meterReadings.length === 0 && !isLoading ? (
        <p className="text-gray-500 text-center py-4">Henüz sayaç okuması bulunmamaktadır.</p>
      ) : (
        <div className="overflow-x-auto shadow border-b border-gray-200 sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Daire
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Sayaç Türü
                </th>
                <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Tüketim
                </th>
                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                  Dönem
                </th>
                <th scope="col" className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">
                  İşlemler
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {meterReadings.map((reading) => {
                // ApartmentId veya apartmentId değerini kontrol et
                const apartmentId = reading.ApartmentId !== undefined ? reading.ApartmentId : reading.apartmentId;
                
                // MeterType veya meterType değerini kontrol et
                const meterType = reading.MeterType !== undefined ? reading.MeterType : reading.meterType;
                
                // Consumption veya consumption değerini kontrol et
                const consumption = reading.Consumption !== undefined ? reading.Consumption : reading.consumption;
                
                // Period veya period değerini kontrol et
                const period = reading.Period !== undefined ? reading.Period : reading.period;
                
                // ReadingId veya readingId değerini kontrol et
                const readingId = reading.ReadingId !== undefined ? reading.ReadingId : reading.readingId;
                
                return (
                  <tr key={readingId} className="hover:bg-gray-50">
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      D: {getApartmentNumber(apartmentId)} (
                      {getBlockName(getBlockIdForApartment(apartmentId))})
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                      {meterType === 'su' ? 'Su' : 'Yakıt'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700 text-right">
                      {consumption} {meterType === 'su' ? 'm³' : 'kWh'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">{period}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-center space-x-2">
                      <button onClick={() => openEditModal(reading)} className="icon-button-indigo" title="Düzenle">
                        <Edit3 size={16} />
                      </button>
                      <button
                        onClick={() => setConfirmDeleteModal({ isOpen: true, readingId: readingId })}
                        className="icon-button-red"
                        title="Sil"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Düzenleme Modalı */}
      {editingReading && (
        <Modal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} title="Sayaç Okumasını Düzenle" size="lg">
          <form onSubmit={handleUpdateMeterReading}>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="editApartmentId" className="label-form-style">Daire</label>
                <select
                  id="editApartmentId"
                  name="apartmentId"
                  value={editingReading.apartmentId}
                  onChange={(e) => setEditingReading({ ...editingReading, apartmentId: e.target.value })}
                  className="input-form-style"
                  required
                >
                  {apartments
                    .filter(apt => {
                      const aptBuildingId = apt.buildingId || apt.BuildingId;
                      return aptBuildingId === currentUser.buildingId;
                    })
                    .map(apt => {
                      const aptId = apt.id || apt.apartmentId || apt.ApartmentId;
                      const aptNumber = apt.apartmentNumber || apt.ApartmentNumber;
                      const blkId = apt.blockId || apt.BlockId;
                      return (
                        <option key={aptId} value={aptId}>
                          {aptNumber} ({getBlockName(blkId)})
                        </option>
                      );
                    })}
                </select>
              </div>
              <div>
                <label htmlFor="editMeterType" className="label-form-style">Sayaç Türü</label>
                <select
                  id="editMeterType"
                  name="meterType"
                  value={editingReading.meterType}
                  onChange={(e) => setEditingReading({ ...editingReading, meterType: e.target.value })}
                  className="input-form-style"
                  required
                >
                  <option value="su">Su</option>
                  <option value="yakit">Yakıt</option>
                </select>
              </div>
              <div>
                <label htmlFor="editConsumption" className="label-form-style">Tüketim (m³/kWh)</label>
                <input
                  type="number"
                  id="editConsumption"
                  name="consumption"
                  step="0.01"
                  value={editingReading.consumption}
                  onChange={(e) => setEditingReading({ ...editingReading, consumption: e.target.value })}
                  className="input-form-style"
                  required
                />
              </div>
              <div>
                <label htmlFor="editPeriod" className="label-form-style">Dönem (YYYY-MM)</label>
                <input
                  type="month"
                  id="editPeriod"
                  name="period"
                  value={editingReading.period}
                  onChange={(e) => setEditingReading({ ...editingReading, period: e.target.value })}
                  className="input-form-style"
                  required
                />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-4">
              <button type="button" onClick={() => setEditModalOpen(false)} className="btn-secondary-style">
                İptal
              </button>
              <button type="submit" className="btn-primary-green-style" disabled={isLoading}>
                Güncelle
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Silme Onayı Modalı */}
      <ConfirmationModal
        isOpen={confirmDeleteModal.isOpen}
        onClose={() => setConfirmDeleteModal({ isOpen: false, readingId: null })}
        title="Sayaç Okumasını Sil"
        message="Bu sayaç okumasını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz."
        onConfirm={handleDeleteMeterReading}
      />

    </div>
  );
};

export default MeterReadingsView;
